import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, signal, computed, input, effect } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { CurrencyPipe, NgStyle } from '@angular/common';
import { LegendPosition } from "@swimlane/ngx-charts";
import { MatDialog } from "@angular/material/dialog";
import { DatePipe } from "@angular/common";
import { FormatTimePipe } from "../../format-time.pipe";
import { SellOffer } from "../../entity/SellOffer";
import { OffersData } from "../../obj/OffersData";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { KilowantContract } from "../../entity/KilowantContract";
import { Stats } from "../../obj/Stats";
import { KilowantAreaPriceHistoryChartComponent } from "../chart/kilowant-charts-home-price-history.component";
import { AbstractBuyerComponent } from "../abstract/buyer.component";

@Component({
    selector: "kilowant-user-home-price-statistics",
    templateUrl: "../../../html/incs/user-home-price-statistics.html",
    imports: [NgStyle, KilowantAreaPriceHistoryChartComponent, CurrencyPipe]
})
export class UserHomePriceStatisticsComponent extends AbstractBuyerComponent {
    // Inputs as signals
    selectedOffer = input<OffersData | null>(null);
    contract = input<KilowantContract | null>(null);
    tiny = input<boolean>(false);
    openMode = input<boolean>(false);

    @Output("viewChanged") changed = new EventEmitter<boolean>();

    @ViewChild("body") bodyElement!: ElementRef;

    // Reactive state
    price = signal<number>(0);
    abs_min_price = signal<number>(0);
    abs_max_price = signal<number>(0);
    min_price = signal<number>(0);
    max_price = signal<number>(0);
    percent = signal<number>(0);
    info = signal<string>("typical");
    info_2 = signal<string>("");
    priceClass = signal<string>("average");
    colorScheme = signal({ domain: ["#D0D0D0", "#A0A0A0"], name: "kilowant", selectable: true, group: null! });
    view = signal<[number, number]>([320, 200]);
    legend = signal<boolean>(true);
    legendTitle = signal<string>("");
    legendPosition = signal<LegendPosition>(LegendPosition.Below);
    xAxis = signal<boolean>(true);
    yAxis = signal<boolean>(true);
    showYAxisLabel = signal<boolean>(false);
    showXAxisLabel = signal<boolean>(false);
    xAxisLabel = signal<string>("Year");
    yAxisLabel = signal<string>("");
    historyData = signal<any[]>([]);
    pointDate = signal<Date | null>(null);
    pointValue = signal<number | null>(null);
    statsCollapse = signal<boolean>(true);
    showStats = signal<boolean>(false);

    // Computed signals
    isNewOffer = computed(() => {
        const offer = this.selectedOffer();
        return offer != null && (offer.priceStats == null || offer.priceStats.length < 2);
    });

    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        protected datepipe: DatePipe,
        protected currencypipe: CurrencyPipe,
        protected formattimepipe: FormatTimePipe,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService
    ) {
        super(router, route, dialog, authService, analyticsService, generalService);
    }

    ngOnInit(): void {
    }

    ngOnChanges(): void {
        this.reloadData();
    }

    reloadData(): void {
        const offer = this.selectedOffer();
        const contract = this.contract();

        if (offer != null) {
            this.price.set(offer.offer.priceForBuyer());
            this.generalService.getPriceStatsPerSellOffer(<SellOffer>offer.offer)
                .subscribe({
                    next: (res: any) => {
                        this.assignPriceStats(res);
                    },
                    error: (err: any) => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    }
                });
            this.generalService.getPriceHistoryPerSellOffer(<SellOffer>offer.offer)
                .subscribe({
                    next: (res: any) => {
                        this.assignPriceHistory(res, null, null);
                    },
                    error: (err: any) => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    }
                });
        } else if (contract != null) {
            this.price.set(contract.priceForBuyer());
            this.generalService.getPriceStatsPerContract(contract)
                .subscribe((res: any) => {
                    this.assignPriceStats(res);
                },
                    err => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    });
            this.generalService.getPriceHistoryPerContract(contract)
                .subscribe({
                    next: (res: any) => {
                        this.assignPriceHistory(res, contract.dateEntered, contract.priceForBuyer());
                    },
                    error: (err: any) => {
                        if (err.status == 400) {
                            this.setErrorText(err.error);
                        }
                    }
                });
        }
    }

    changeCollapse(): void {
        this.statsCollapse.update(value => !value);
        this.changed.emit(true);
    }

    assignPriceStats(res: Stats): void {
        let absMin = res.min;
        let absMax = res.max;
        const currentPrice = this.price();

        if (currentPrice < absMin) {
            absMin = currentPrice;
        }
        if (currentPrice > absMax) {
            absMax = currentPrice;
        }
        const diff = absMax - absMin;

        this.abs_min_price.set(absMin);
        this.abs_max_price.set(absMax);
        this.min_price.set(absMin + diff / 3);
        this.max_price.set(absMax - diff / 3);
        const averagePrice = (absMax + absMin) / 2;

        if (diff == 0 || res.length < 2) {
            this.percent.set(50);
        } else {
            this.percent.set(Math.round((currentPrice - absMin) * 100 / diff));
        }

        this.info.set("typical");
        this.priceClass.set("average");

        if (currentPrice <= this.min_price()) {
            this.info.set("low");
            this.priceClass.set("low");
        }
        if (currentPrice >= this.max_price()) {
            this.info.set("high");
            this.priceClass.set("high");
        }

        if (averagePrice == currentPrice) {
            this.info_2.set("exactly coincide with usual");
        } else if (averagePrice > currentPrice) {
            let percentDiff = Math.round((averagePrice - currentPrice) / averagePrice * 100);
            if (percentDiff == 0) {
                percentDiff = Math.round((averagePrice - currentPrice) / averagePrice * 1000) / 10;
            }
            const diffAmount = this.currencypipe.transform((averagePrice - currentPrice), 'USD', 'symbol', '1.5-5');
            this.info_2.set(`${diffAmount} (${percentDiff}%) cheaper than the historic average`);
        } else {
            let percentDiff = Math.round((currentPrice - averagePrice) / averagePrice * 100);
            if (percentDiff == 0) {
                percentDiff = Math.round((currentPrice - averagePrice) / averagePrice * 1000) / 10;
            }
            const diffAmount = this.currencypipe.transform((currentPrice - averagePrice), 'USD', 'symbol', '1.5-5');
            this.info_2.set(`${diffAmount} (${percentDiff}%) more expensive than the historic average`);
        }
    }

    assignPriceHistory(res: any, pDate: Date | null, pValue: number | null): void {
        this.view.update(v => [this.bodyElement.nativeElement.offsetWidth, v[1]]);
        const series: any[] = [];

        for (let i = 0; i < res.length; i++) {
            series.push({
                value: res[i].value,
                name: res[i].key
            });
        }

        if (pDate != null) {
            this.pointDate.set(pDate);
            this.pointValue.set(pValue);
        } else if (res != null && res.length > 0) {
            this.pointDate.set(res[res.length - 1].key);
            this.pointValue.set(res[res.length - 1].value);
        }

        this.historyData.set([{
            name: "Price History",
            series: series
        }]);
        if (res.length > 2) {
            this.showStats.set(true);
        }
    }

    ifPriceStatsAreActive(): boolean {
        const offer = this.selectedOffer();
        if (offer?.priceStats == null) return false;
        if (offer.priceStats.min == offer.priceStats.max) return false;
        if (offer.priceStats.length < 2) return false;
        return true;
    }

    ngOnDestroy() { }
}